import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Header,
  ModalWrapper,
  Placeholder,
  ScrollView,
  Tabs,
  TabsItem,
} from '$uikit';
import { useCurrentUserInfo, useDesktop, useTranslator } from '$hooks';
import './Cloud.sass';
import {
  IcClose28,
  IcCloudUpload28,
  IcDelete28,
  IcEdit28,
  IcFolderCross28,
  IcMessage28,
  IcPlay28,
  IcSort28,
  IcUpload28,
} from '$assets';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { cloudActions, cloudSelector } from '$store/cloud';
import { AttachPhoto, CloudAlbumItemModel, CloudMediaTabs } from '$store/models';
import { Loader, Viewer } from '$shared/components';
import { useRoute } from 'react-router5';
import router from '$navigation/router';
import { CLOUD, OTHER } from '$shared/constants/pages';
import { CLOUD_ALL_FILES_ALBUM_ID } from '$shared/constants/cloud';
import { goBack, goTo, showModal } from '$navigation/helper';
import { mainActions, mainSelector } from '$store/main';
import { CloudBottomSheet } from '$core/Cloud/CloudBottomSheet/CloudBottomSheet';
import { attachActions } from '$store/attach';
import { Write } from '$core';
import {
  _confirm,
  classNames as cn,
  format,
  getFormattedTime,
  ATTACHMENTS_COUNT_LIMIT,
} from '$utils';
import { CloudAlbums } from './CloudAlbums/CloudAlbums';
import { CloudPlaceholder } from '$core/Cloud/CloudPlaceholder/CloudPlaceholder';
import { toastActions } from '$store/toast';
import { ViewerItem } from '$shared/components/Viewer/VieverItem/ViewerItem.interface';
import { CloudFilesUpload } from '$core/Cloud/CloudFilesUpload/CloudFilesUpload';
import { CloudPlaceholderProps } from '$core/Cloud/CloudPlaceholder/CloudPlaceholder.interface';
import { DynamicComponent } from '$core/DynamicComponent';
import { Api } from '$api';
import { batchActions } from '$store';

enum InitialAlbums {
  UPLOADED = 'uploaded',
  MESSAGES = 'messages',
  POSTS = 'posts',
}

interface CloudCoverProps {
  albumId: string;
}

export const CloudCoverUpload: React.FC<CloudCoverProps> = ({ albumId }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { itemsInfo } = useSelector(cloudSelector);
  const [inputKey, setInputKey] = useState(0);

  const albumInfo = itemsInfo[albumId];

  const handleChange = useCallback(
    ({ target }) => {
      if (!target.files.length) {
        return;
      }

      const file = target.files[0];

      dispatch(
        cloudActions.uploadCover({
          albumId: albumId,
          file: file,
        }),
      );
      setInputKey(Date.now());
    },
    [dispatch, albumId, albumInfo],
  );

  return (
    <>
      <ScrollView className="Cover">
        <label
          className={cn('Cover__editor', {
            loading: false,
            uploaded: false,
            wide: false,
            big_padding_top: false,
            small_padding_top: true,
          })}
        >
          <div className={'Cover__information'}>
            <IcUpload28 />
            <div className="Cover__text">{t('profile_upload_cover')}</div>
          </div>
          <input
            key={inputKey}
            className={'Cover__attach'}
            type="file"
            accept="image/*"
            onChange={handleChange}
          />
        </label>
      </ScrollView>
    </>
  );
};

export const Cloud = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const {
    albums,
    itemsInfo,
    isAlbumsLoading,
    isItemsLoading,
    isItemsLoadingMore,
    selectedItems,
    nextFrom,
    pickerId,
    routeName,
    filter,
  } = useSelector(cloudSelector);
  const { mVerified } = useSelector(mainSelector);
  const user = useCurrentUserInfo();
  const { route } = useRoute();
  const isDesktop = useDesktop();
  const params = route.params;
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentViewerItem, setCurrentViewerItem] = useState<number | null>(null);
  const [isWriteModalOpen, setIsWriteModalOpen] = useState(false);
  const [isActionShow, setIsActionShow] = useState(false);
  const albumId = params.albumId;
  const isAlbumSelected = 'albumId' in params && albumId;
  const currentAlbumId = isAlbumSelected ? albumId : CLOUD_ALL_FILES_ALBUM_ID;
  const albumInfo = itemsInfo[currentAlbumId];
  const items = albumInfo?.items;
  const isAsc = albumInfo?.isAsc;
  const albumTitle = albumInfo?.title;
  const isAlbumItemsLoading = albumInfo?.isLoading;
  const isSelectingMode = !!selectedItems.length;
  const isCloudEmpty = !albums.length && !isAlbumsLoading;

  const limit = route.meta?.options?.limit || ATTACHMENTS_COUNT_LIMIT;

  useEffect(() => {
    loadItems(isAlbumSelected ? albumId : null, isAsc);

    return () => {
      dispatch(cloudActions.reset());
    };
  }, []);

  useEffect(() => {
    !isItemsLoading && loadItems(isAlbumSelected ? albumId : null, isAsc);
    !isAlbumSelected && dispatch(cloudActions.loadAlbums());
    dispatch(cloudActions.setAlbumId(currentAlbumId));
    setSelectedItems([]);
  }, [albumId, isAlbumSelected]);

  useEffect(() => {
    if (!mVerified) {
      goTo(OTHER);
    }
  }, [mVerified]);

  const handleCloseViewer = () => {
    setCurrentViewerItem(null);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseViewer();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getVideoSource = (path: string) => JSON.parse(path)[0]?.path;

  const getItemSize = (item: CloudAlbumItemModel) => {
    const { elementType } = item;

    const itemSize = {
      width: 0,
      height: 0,
    };

    if (elementType === 'photo') {
      itemSize.width = item.photoExtra?.width || 300;
      itemSize.height = item.photoExtra?.height || 300;
    } else {
      itemSize.width = item.videoExtra?.width || 300;
      itemSize.height = item.videoExtra?.height || 300;
    }

    return itemSize;
  };

  const viewerItems = useMemo(() => {
    return items?.map((item) => {
      const { id, elementType, path } = item;

      const viewerItem: ViewerItem = {
        id,
        source: path,
        ...getItemSize(item),
      };

      if (elementType === 'video') {
        viewerItem.source = getVideoSource(path);
        viewerItem.id = -id;
        viewerItem.thumbnail = item.videoExtra?.posterPath;
      }

      return viewerItem;
    });
  }, [items]);

  const loadItems = (
    albumId: string | null,
    isAsc = false,
    startFrom = 0,
    isMore?: boolean,
    filter?: CloudMediaTabs,
  ) => {
    dispatch(
      cloudActions.loadItems({
        albumId,
        isAsc,
        startFrom,
        isMore,
        filter: filter || CloudMediaTabs.ALL,
      }),
    );
  };

  const handleTabChange = (tab: CloudMediaTabs) => {
    loadItems(albumId, isAsc, 0, false, tab);
  };

  const setSelectedItems = (items: CloudAlbumItemModel[]) => {
    dispatch(cloudActions.setSelectedItems(items));
  };

  const toggleEditMode = () => {
    // setIsEditMode(!isEditMode);
  };

  const getAlbumTitle = (title?: string) => {
    if (!title) {
      return '';
    }

    if (title?.toUpperCase() in InitialAlbums) {
      return t(`cloud_storage_album_${title.toLowerCase()}`);
    }

    return title;
  };

  const getAttachItems = (): AttachPhoto[] => {
    return selectedItems.map((item) => {
      const { elementId, elementType, path } = item;
      const photo = elementType === 'photo' ? path : item.videoExtra?.posterPath;

      return {
        photoId: elementId,
        photoLarge: photo || '',
        photoMedium: photo || '',
        ...getItemSize(item),
        isVideo: elementType === 'video',
      };
    });
  };

  const handleClosePopup = (isClearSelectedItems?: boolean) => {
    dispatch(mainActions.setPopup(null));
    isClearSelectedItems && setSelectedItems([]);
  };

  const handleDeleteItems = () => {
    dispatch(
      cloudActions.deleteItemsFromAlbum({
        items: selectedItems,
      }),
    );
    handleClosePopup(true);
  };

  const handlePublishItems = () => {
    dispatch(
      attachActions.pushPhotos({ pickerId: `post_${user.id}`, photos: getAttachItems() }),
    );
    handleClosePopup();
    showModal(`new_post${user.id}`);
  };

  const handleSendItems = (pickerId: string) => {
    dispatch(attachActions.pushPhotos({ pickerId, photos: getAttachItems() }));
  };

  const onShareButtonClick = () => {
    if (selectedItems.length > limit) {
      dispatch(
        toastActions.setToastFail(
          t('photos_limit_error_custom', {
            limit,
          }),
        ),
      );
      return;
    }

    if (pickerId && routeName) {
      // savedContentActions.length &&
      //   // savedContentActions.forEach((action) => {
      //   //   dispatch({ type: action.type, payload: action.payload });
      //   // });

      handleSendItems(pickerId);

      goBack();
      return;
    }

    dispatch(
      mainActions.setPopup(
        <CloudBottomSheet
          type="share"
          albumItems={selectedItems}
          actionItems={[
            {
              title: t('cloud_storage_send_message'),
              onClick: () => {
                handleClosePopup();
                setIsWriteModalOpen(true);
              },
              icon: <IcMessage28 />,
            },
            {
              title: t('cloud_storage_create_post'),
              onClick: handlePublishItems,
              icon: <IcEdit28 />,
            },
          ]}
        />,
      ),
    );
  };

  const getPlaceholderProps = () => {
    const placeholderProps: CloudPlaceholderProps = {
      title: t('cloud_placeholder_general_title'),
      description: t('cloud_placeholder_general_description'),
    };

    const lowerCasedAlbumTitle = albumTitle?.toLowerCase();

    if (isAlbumSelected) {
      placeholderProps.title = t(`cloud_placeholder_album_title`);

      if (
        lowerCasedAlbumTitle === InitialAlbums.MESSAGES ||
        lowerCasedAlbumTitle === InitialAlbums.POSTS
      ) {
        placeholderProps.description = t(
          `cloud_placeholder_${lowerCasedAlbumTitle}_description`,
        );
      } else {
        placeholderProps.description = t(`cloud_placeholder_album_description`);
      }
    } else {
      placeholderProps.children = (
        <Button style={{ marginTop: '32px' }} onClick={() => setIsActionShow(true)}>
          {t('cloud_upload_media')}
        </Button>
      );
    }

    return placeholderProps;
  };

  const handleUploadAction = () => {
    setIsActionShow(false);
  };

  function renderItems() {
    if (isItemsLoading || isAlbumItemsLoading) {
      return (
        <Placeholder centered>
          <Loader isWrapped />
        </Placeholder>
      );
    }

    if (!items?.length && !isItemsLoading && !isAlbumItemsLoading) {
      return <CloudPlaceholder {...getPlaceholderProps()} />;
    }

    return (
      <div className="Cloud__files__content">
        {items.map((prepItem, index) => {
          let selectedItemCount = '';
          const isItemSelected = selectedItems.find((item, index) => {
            if (item.id === prepItem.id) {
              selectedItemCount = (index + 1).toString();
              return true;
            }
          });
          const itemCreatedDate = new Date(prepItem.createdAt);

          let content: ReactNode = null;

          if (prepItem.elementType === 'photo') {
            content = <img src={prepItem.path} alt="" />;
          } else if (prepItem.elementType === 'video') {
            const { videoExtra } = prepItem;

            content = (
              <>
                <div className="ExternalVideo__iframe">
                  <div className="ExternalVideo__play_btn">
                    <IcPlay28 />
                  </div>
                </div>
                <img src={videoExtra?.posterPath || ''} alt="" />
                {!!videoExtra?.duration && (
                  <div className="ExternalVideo__duration_label">
                    {getFormattedTime(videoExtra.duration)}
                  </div>
                )}
              </>
            );
          }

          return (
            <div
              className="Cloud__files__item"
              key={prepItem.id}
              onClick={() => setCurrentViewerItem(index)}
            >
              <div className="Cloud__files__item_bg" />
              <div className="Cloud__files__item_date">
                {format(
                  itemCreatedDate,
                  `d MMMM ${
                    itemCreatedDate.getFullYear() === new Date().getFullYear()
                      ? ''
                      : 'yyyy'
                  }`,
                )}
              </div>
              <div
                className="Cloud__files__item_helper_wrap"
                onClick={(e) => {
                  e.stopPropagation();
                  isItemSelected
                    ? setSelectedItems([
                        ...selectedItems.filter((item) => item.id !== prepItem.id),
                      ])
                    : setSelectedItems([...selectedItems, prepItem]);
                }}
              >
                <div
                  className={`Cloud__files__item_helper ${
                    isItemSelected ? '_selected' : ''
                  }`}
                  style={{
                    fontSize: `${
                      selectedItemCount.length > 1
                        ? 18 - selectedItemCount.length * 2
                        : 18
                    }px`,
                  }}
                >
                  {selectedItemCount}
                </div>
              </div>
              {prepItem.bought && (
                <div className="Cloud__item_sold">{t('cloud_storage_sold')}</div>
              )}
              {content}
            </div>
          );
        })}
      </div>
    );
  }

  function renderMainWrapper() {
    if (isCloudEmpty) {
      return null;
    }

    return (
      <div className="Cloud__main__wrapper">
        {!isAlbumSelected ? (
          <div className={`Cloud__albums__wrapper ${isEditMode ? '_isEditMode' : ''}`}>
            <div className="Cloud__header _albums">
              <h2>
                {isEditMode && (
                  <div className="Cloud__header__icon" onClick={toggleEditMode}>
                    <IcClose28 />
                  </div>
                )}
                {t(isEditMode ? 'cloud_storage_customize_order' : 'cloud_storage_albums')}
              </h2>
              {!isEditMode && (
                <div
                  className="Cloud__header__btn"
                  onClick={() => showModal('cloud_album_create')}
                >
                  {t('cloud_storage_create_new_album')}
                </div>
              )}
              {/*{!!albums.length && (*/}
              {/*  <div className="Cloud__header__icon" onClick={toggleEditMode}>*/}
              {/*    {isEditMode ? (*/}
              {/*      <IcCheck28 />*/}
              {/*    ) : (*/}
              {/*      <IcEdit28 style={{ color: 'var(--foreground-tertiary)' }} />*/}
              {/*    )}*/}
              {/*  </div>*/}
              {/*)}*/}
            </div>
            {!isAlbumSelected && (
              <div className="Cloud__albums__content">
                <CloudAlbums
                  albums={albums}
                  onAlbumClick={(albumId) => {
                    loadItems(albumId.toString(), itemsInfo[albumId]?.isAsc);
                    router.navigate(CLOUD, { albumId });
                  }}
                  isEditMode={isEditMode}
                  isLoading={isAlbumsLoading && !albums.length}
                  getAlbumTitle={getAlbumTitle}
                />
              </div>
            )}
          </div>
        ) : (
          <CloudCoverUpload albumId={currentAlbumId} />
        )}

        <div className="Cloud__header _files">
          <h2>
            {isAlbumSelected ? getAlbumTitle(albumTitle) : t('cloud_storage_all_media')}
          </h2>
          {!!items?.length && (
            <div
              className="Cloud__header__icon"
              onClick={() => {
                if (!isItemsLoading && !isAlbumItemsLoading) {
                  loadItems(isAlbumSelected ? albumId : null, !isAsc, 0, false, filter);
                  setSelectedItems([]);
                }
              }}
              style={{
                cursor: !isItemsLoading && !isAlbumItemsLoading ? 'pointer' : 'default',
              }}
            >
              <IcSort28
                style={{
                  color: isAsc
                    ? 'var(--constant-brand-primary)'
                    : 'var(--foreground-tertiary)',
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderContent() {
    if (!isAlbumSelected && isAlbumsLoading && !albums.length) {
      return <Loader isWrapped />;
    }

    return (
      <>
        <ScrollView
          className="Cloud"
          onLoadMore={() =>
            loadItems(isAlbumSelected ? albumId : null, isAsc, nextFrom, true, filter)
          }
          canLoadMore={!isItemsLoading && !isItemsLoadingMore && !!nextFrom}
          isLoadingMore={isItemsLoadingMore}
        >
          {renderMainWrapper()}
          {!isCloudEmpty && (
            <Tabs
              selected={filter}
              onChange={(tab) => handleTabChange(tab as CloudMediaTabs)}
              skipMargin
            >
              {Object.values(CloudMediaTabs).map((tab, index) => (
                <TabsItem key={index} value={tab}>
                  {t(`cloud_storage_tab_${tab}`)}
                </TabsItem>
              ))}
            </Tabs>
          )}
          <div className="Cloud__files__wrapper">{renderItems()}</div>
        </ScrollView>
        {currentViewerItem !== null && (
          <Viewer
            items={viewerItems}
            active={currentViewerItem}
            onChange={setCurrentViewerItem}
            onClose={handleCloseViewer}
            mobile={!isDesktop}
          />
        )}
        {isSelectingMode && (
          <Button className="Cloud__bottom_btn" onClick={onShareButtonClick}>
            {t('cloud_storage_share')}
          </Button>
        )}
        {isWriteModalOpen && (
          <ModalWrapper name="write" onOverlayClick={() => setIsWriteModalOpen(false)}>
            <DynamicComponent>
              <Write
                onCellClick={(userId) => handleSendItems(`im_${userId}`)}
                onCloseButtonClick={() => setIsWriteModalOpen(false)}
              />
            </DynamicComponent>
          </ModalWrapper>
        )}
        <CloudFilesUpload
          isOpen={isActionShow}
          onActionChange={handleUploadAction}
          onClose={() => setIsActionShow(false)}
        />
      </>
    );
  }

  return (
    <>
      <Header
        showBackButton={!isSelectingMode}
        showCloseButton={isSelectingMode}
        leftContent={
          isSelectingMode ? (
            <div className="Cloud__header_left">
              <IcClose28 onClick={() => setSelectedItems([])} />
              {selectedItems.length} {t('cloud_storage_selected')}
            </div>
          ) : null
        }
        rightIcons={[
          {
            icon: <IcDelete28 />,
            onClick: () =>
              dispatch(
                mainActions.setPopup(
                  <CloudBottomSheet
                    type="delete"
                    albumItems={selectedItems}
                    actionItems={[
                      {
                        title: t('cloud_storage_remove_from_album'),
                        onClick: handleDeleteItems,
                        icon: <IcFolderCross28 />,
                      },
                    ]}
                  />,
                ),
              ),
            visible: isSelectingMode, // !!isAlbumSelected &&
          },
          {
            icon: <IcCloudUpload28 />,
            onClick: () => setIsActionShow(true),
            visible: !isSelectingMode,
          },
          {
            icon: <IcDelete28 />,
            onClick: () => {
              _confirm(t('cloud_deleted_album_confirm'), () => {
                dispatch(toastActions.setToastLoading());
                Api.get<any, any>(`/albums/${albumId}/delete`)
                  .then(() => {
                    dispatch(
                      batchActions(
                        toastActions.setToastSuccess(),
                        cloudActions.removeItemFromUI(albumId),
                      ),
                    );
                    goBack();
                    dispatch(
                      cloudActions.loadItems({
                        filter,
                        albumId: '',
                        isAsc: false,
                        startFrom: 0,
                      }),
                    );
                  })
                  .catch((e) => {
                    dispatch(toastActions.setToastFail(e.message));
                  });
              });
            },
            visible: !!isAlbumSelected && !isSelectingMode && !!albumInfo,
            //!albumInfo[albumId]?.isDefault,
          },
        ]}
      >
        {!isSelectingMode && t(isAlbumSelected ? 'cloud_storage_album' : 'cloud_storage')}
      </Header>
      {renderContent()}
    </>
  );
};
